import { TraceData } from './types';
import { generateId, getTimestamp } from './utils';
import { Span } from './span';

/**
 * Trace 类 - 表示一次完整的追踪链路
 */
export class Trace {
  private _traceId: string;
  private _operationName: string;
  private _startTime: number;
  private _endTime?: number;
  private _spans: Span[] = [];
  private _rootSpan?: Span;
  private _finished: boolean = false;

  constructor(operationName: string) {
    this._traceId = generateId();
    this._operationName = operationName;
    this._startTime = getTimestamp();
  }

  /**
   * 获取 Trace ID
   */
  get traceId(): string {
    return this._traceId;
  }

  /**
   * 获取操作名称
   */
  get operationName(): string {
    return this._operationName;
  }

  /**
   * 获取开始时间
   */
  get startTime(): number {
    return this._startTime;
  }

  /**
   * 获取结束时间
   */
  get endTime(): number | undefined {
    return this._endTime;
  }

  /**
   * 获取持续时间（毫秒）
   */
  get duration(): number {
    const endTime = this._endTime || getTimestamp();
    return endTime - this._startTime;
  }

  /**
   * 获取所有 Spans
   */
  get spans(): Span[] {
    return [...this._spans];
  }

  /**
   * 获取根 Span
   */
  get rootSpan(): Span | undefined {
    return this._rootSpan;
  }

  /**
   * 是否已结束
   */
  get finished(): boolean {
    return this._finished;
  }

  /**
   * 获取当前 Span
   */
  getCurrentSpan(): Span | undefined {
    return this._spans[this._spans.length - 1];
  }

  /**
   * 创建根 Span
   */
  createRootSpan(operationName: string): Span {
    if (this._rootSpan) {
      console.warn('Root span already exists, creating child span instead');
      return this.createSpan(operationName, this._rootSpan);
    }
    const span = new Span(operationName);
    this._rootSpan = span;
    this._spans.push(span);
    return span;
  }

  /**
   * 创建子 Span
   */
  createSpan(operationName: string, parentSpan?: Span): Span {
    if (this._finished) {
      console.warn('Cannot create span in finished trace');
      throw new Error('Cannot create span in finished trace');
    }

    // 如果没有指定父 Span，使用根 Span
    const parent = parentSpan || this._rootSpan;
    const parentSpanId = parent ? parent.spanId : undefined;

    const span = new Span(operationName, parentSpanId);
    this._spans.push(span);
    return span;
  }

  /**
   * 结束 Trace
   */
  finish(): void {
    if (this._finished) {
      console.warn('Trace already finished');
      return;
    }

    // 确保所有 spans 都已结束
    this._spans.forEach((span) => {
      if (!span.finished) {
        span.finish();
      }
    });

    this._endTime = getTimestamp();
    this._finished = true;
  }
  
  /**
   * 转换为 TraceData
   */
  toData(serviceName: string, serviceInstance: string, serviceVersion: string): TraceData {
    return {
      traceId: this._traceId,
      serviceName,
      serviceInstance,
      serviceVersion,
      spans: this._spans.map((span) => span.toData()),
      startTime: this._startTime,
      endTime: this._endTime || getTimestamp(),
      duration: this.duration,
    };
  }
}

